<template>
    <form-wrapper @form-submit="submit">
        <form-row v-if="!prefill?.id">
            <form-cell width="half">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.discount_offer.offer.label')"
                    :placeholder="$t('forms.offer.discount_offer.offer.placeholder')"
                    :options="offersOptions"
                    valuePath="offerId"
                    :isSearchable="true"
                    @change="offerChanged"
                ></select-input>
            </form-cell>
            <form-cell width="half">
                <multi-select-input
                    :formData="data"
                    valuePath="offerCategoryIds"
                    :label="$t('forms.offer.discount_offer.category.label')"
                    :placeholder="$t('forms.offer.discount_offer.category.placeholder')"
                    :isDisabled="!data.offerId"
                    :isRequired="true"
                    :options="offerCategories"
                    :showSelectAllOption="true"
                    @change="change"
                ></multi-select-input>
            </form-cell>
        </form-row>
        <form-row v-else>
            <form-cell>
                <text-input
                    :formData="data"
                    :label="$t('forms.offer.discount_offer.category.label')"
                    :placeholder="$t('forms.offer.discount_offer.category.placeholder')"
                    :isRequired="true"
                    :isDisabled="true"
                    type="text"
                    valuePath="offerCategoryId"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell>
                <text-input
                    :formData="data"
                    :label="$t('forms.offer.discount_offer.title.label')"
                    :placeholder="$t('forms.offer.discount_offer.title.placeholder')"
                    :isRequired="true"
                    type="text"
                    valuePath="title"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half">
                <datepicker-input
                    ref="dateFrom"
                    :formData="data"
                    :label="$t('forms.offer.discount_offer.date_from.label')"
                    :placeholder="$t('forms.offer.discount_offer.date_from.placeholder')"
                    valuePath="dateFrom"
                    :isRequired="true"
                    :prefillValue="dateFrom"
                    :lowerLimit="new Date()"
                    :isSubmittable="false"
                    :upperLimit="upperLimit"
                    @date-changed="dateFromChanged"
                ></datepicker-input>
            </form-cell>
            <form-cell width="half">
                <datepicker-input
                    ref="dateTo"
                    :formData="data"
                    :label="$t('forms.offer.discount_offer.date_to.label')"
                    :placeholder="$t('forms.offer.discount_offer.date_to.label')"
                    valuePath="dateTo"
                    :isRequired="true"
                    :prefillValue="dateTo"
                    :isSubmittable="false"
                    :lowerLimit="lowerLimit"
                    @date-changed="dateToChanged"
                ></datepicker-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.offer.discount_offer.percentage.label')"
                    :placeholder="$t('forms.offer.discount_offer.percentage.placeholder')"
                    valuePath="discountPercentage"
                    :isRequired="true"
                    type="number"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>
        <template #footer>
            <common-content-spacer height="medium">
                <p>*{{ $t('forms.offer.discount_offer.discount_info') }}*</p>
                <common-buttons>
                    <common-button type="primary" :label="$t('common.microcopy.save')"></common-button>
                </common-buttons>
            </common-content-spacer>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import OfferAPI from 'api/OfferAPI'
    import { dateFormat } from '~/helpers'

    export default {
        mixins: [FormMixin],
        props: {
            offers: { type: Array, required: true },
            prefill: { type: Object, default: undefined },
        },
        data() {
            return {
                upperLimit: null,
                lowerLimit: new Date(),
                offerCategories: [],
                dateFrom: undefined,
                dateTo: undefined,
            }
        },

        inject: ['isCompany'],

        computed: {
            offersOptions() {
                return this.offers
                    ?.filter((item) => item.offerCategories.length)
                    .map((offer) => {
                        return {
                            label: offer.titleTextContent.originalText,
                            value: offer.id,
                        }
                    })
            },
        },

        methods: {
            dateFromChanged(value) {
                if (value !== undefined) {
                    this.lowerLimit = dateFormat(value)
                    this.setValue('dateFrom', value)
                } else {
                    this.lowerLimit = new Date()
                    delete this.data['dateFrom']
                }
            },
            dateToChanged(value) {
                if (value !== undefined) {
                    this.upperLimit = dateFormat(value)
                    this.setValue('dateTo', value)
                } else {
                    this.upperLimit = null
                    delete this.data['dateTo']
                }
            },

            offerChanged(value) {
                this.change(value)
                this.setCategories()
            },

            setCategories() {
                this.offerCategories = []
                this.offerCategories = this.offers
                    .find((offer) => offer.id === this.data.offerId)
                    .offerCategories.map((category) => {
                        return {
                            value: category.id,
                            label: category.titleTextContent.originalText,
                        }
                    })
                this.data.offerCategoryIds = this.offerCategories.map((category) => category.value)
            },

            submit() {
                const request = {
                    title: this.data.title,
                    dateFrom: this.data.dateFrom,
                    dateTo: this.data.dateTo,
                    discountPercentage: `${this.data.discountPercentage / 100}`,
                }

                if (this.prefill?.id) {
                    request.offerCategoryId = this.prefill.offerCategory.id
                    const req = this.isCompany ? OfferAPI.updateCompanyDiscount : OfferAPI.updateUserDiscount
                    req(request, this.prefill.id, this.$route.params.id)
                        .then(() => {
                            this.$toast.success(this.$t('common.microcopy.success'))
                            this.emitEvent({ name: 'discount-added' })
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('common.errors.common_error'))
                        })
                } else {
                    request.offerCategoryIds = this.data.offerCategoryIds
                    const req = this.isCompany ? OfferAPI.createCompanyDiscount : OfferAPI.createUserDiscount
                    req(request, this.$route.params.id)
                        .then(() => {
                            this.$toast.success(this.$t('common.microcopy.success'))
                            this.emitEvent({ name: 'discount-added' })
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('common.errors.common_error'))
                        })
                }
            },
        },

        watch: {
            prefill() {
                if (this.prefill?.dateFrom) {
                    this.dateFrom = this.prefill.dateFrom
                }
                if (this.prefill?.dateTo) {
                    this.dateTo = this.prefill.dateTo
                }
            },
        },

        mounted() {
            if (this.prefill?.id) {
                this.prefillData(this.prefill)
                this.data.discountPercentage = this.data.discountPercentage * 100
            }
            if (this.prefill?.dateFrom) {
                this.dateFrom = this.prefill.dateFrom
            }
            if (this.prefill?.dateTo) {
                this.dateTo = this.prefill.dateTo
            }

            if (this.prefill?.offerCategory?.title) {
                this.data.offerCategoryId = this.prefill?.offerCategory.title
            }
        },
    }
</script>
